// "Stop taking new work" and "Start taking new work" from a Machines card's menu (#6560,
// worded by #6563 v2) — the state half, split from the Fleet route. Stop is
// `POST /v1/runners/:id/drain`, Start `POST /v1/runners/:id/undrain`
// (`lib/box-lifecycle.ts`), the same two writes the details panel's `BoxDrainPanel` sends.
//
// NAMED THE DRAIN IN CODE (#6753). This module was machine-pause.ts (deleted; createMachinePause,
// PausableMachine) while its copy already read "Stop taking new work" (#6563 v2). #6753
// put a JOB pause on the same card (`lib/job-controls.ts`), so "pause" in code now means that
// one thing, and this write is the drain everywhere: module, types, menu ids (`drain`,
// `undrain`) and handlers (`onDrain`, `onUndrain`). The `fleet.machines.pause_*` copy keys
// are unchanged.
//
// A DRAIN ASKS FIRST, AN UNDRAIN DOES NOT. A drain takes the machine out of the fleet and
// puts the task it is working on back in line, so a stray click must not do it
// (`createConfirmGate`, `apps/dashboard/CLAUDE.md`; the details panel confirms a drain
// too). An undrain only lets a checking-in machine take work again, so it is one click,
// as in that panel. The prototype drains on one click; this is the deliberate deviation.
//
// The menu offers Stop or Start taking new work off the machine's real status (`paused` is
// drained, brief quirk 2), never a click-tracked flag. Every write is followed by the caller's
// re-read (`onWritten`); the toast says what the SERVER did, from its answer.

import { t } from '../i18n';
import { type BoxDrained, type BoxUndrained, drainBox, undrainBox } from './box-lifecycle';
import { type ConfirmGate, createConfirmGate } from './confirm-gate';
import { createMutation } from './mutation';

/**
 * What a drain DID, in the server's terms (#6563 v2 drain vocabulary): already out, out
 * with its task moved back in line, or out. One seam, read by this menu's toast and by
 * the details panel's notice (`createBoxActions`), so the two never word it apart.
 */
export function drainNotice(name: string, result: BoxDrained): string {
  if (!result.flipped_offline && result.reenqueued_task_ids.length === 0) {
    return t('fleet.machines.pause_already_toast', { name });
  }
  if (result.reenqueued_task_ids.length > 0) {
    return t('fleet.machines.paused_moved_toast', { name });
  }
  return t('fleet.machines.paused_toast', { name });
}

/** What an undrain did: cleared a drain mark, or found none. */
export function undrainNotice(name: string, result: BoxUndrained): string {
  return t(
    result.cleared ? 'fleet.machines.resumed_toast' : 'fleet.machines.resume_already_toast',
    {
      name,
    },
  );
}

/** What the menu acts on: the id it writes and the name the toast says. */
export type DrainableMachine = { readonly id: string; readonly display_name: string };

export type MachineDrain = {
  /** Feed `pending()` to the drain `<ConfirmDialog/>`. */
  readonly drain: ConfirmGate<DrainableMachine>;
  readonly undrain: (machine: DrainableMachine) => void;
};

export function createMachineDrain(input: {
  readonly token: string | null;
  /** The server answered: say so, and re-read. */
  readonly onDone: (message: string) => void;
  readonly onError: (message: string) => void;
  readonly onWritten: () => void;
}): MachineDrain {
  const drain = createMutation({
    call: async (m: DrainableMachine) => ({ m, result: await drainBox(m.id, input.token) }),
    fallback: t('fleet.machines.pause_error'),
    onSuccess: ({ m, result }) => input.onDone(drainNotice(m.display_name, result)),
    onError: (message) => input.onError(message),
    onSettled: () => input.onWritten(),
  });
  const undrain = createMutation({
    call: async (m: DrainableMachine) => ({ m, result: await undrainBox(m.id, input.token) }),
    fallback: t('fleet.machines.resume_error'),
    onSuccess: ({ m, result }) => input.onDone(undrainNotice(m.display_name, result)),
    onError: (message) => input.onError(message),
    onSettled: () => input.onWritten(),
  });
  return {
    drain: createConfirmGate<DrainableMachine>({ act: (m) => drain.run(m) }),
    undrain: (m) => void undrain.run(m),
  };
}
